import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Document, Page } from "react-pdf";
import { DocumentViewer, type EvidenceHighlight, unresolvedHighlightIndexes } from "@/components/ui/document-viewer";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { useEvidenceTooltip, EvidenceTooltip } from "@/components/ui/evidence-tooltip";
import type { ProcessedFileIdentifier, ProcessedFileText } from "../inbound-data/useProcessedFiles";
import { IndicationsPanel } from "./IndicationsPanel";
import { buildIndicationHighlights, type FocusedMention, mentionKeyOf } from "./indicationHighlights";
import type { IndicationVerdictView } from "./useIndicationVerdicts";

interface ProcessedFilesTabsProps {
  files: ProcessedFileText[];
  isLoading: boolean;
  error: string | null;
  /** All evidence highlights across all codes — always shown */
  allHighlights?: EvidenceHighlight[];
  /** Which file tab to auto-switch to (for scroll-to-quote) */
  activeFileIdentifier?: string;
  /** Monotonically increasing counter that triggers scroll-into-view */
  scrollTrigger?: number;
  /** The encounter's indication verdicts; the Indications tab and the outlines show only when there are some. */
  indications?: IndicationVerdictView[];
}

const INDICATIONS_TAB = "indications";
const NO_INDICATIONS: IndicationVerdictView[] = [];

/**
 * File types whose text is raw OCR / clinical report content. Rendering these as
 * Markdown would mangle stray `#`, `-`, `1.` and `|` characters, so they stay plain.
 * Every other type is Markdown-formatted by the pipeline and renders as Markdown.
 */
const PLAIN_TEXT_FILE_TYPES = new Set<ProcessedFileIdentifier>([
  "endoscopy_procedure",
  "endoscopy_histology",
  "comorbidity_doc",
  "discharge_letter",
  "cancer_mdt",
]);

function isMarkdownFile(file: ProcessedFileText): boolean {
  return !PLAIN_TEXT_FILE_TYPES.has(file.fileIdentifier);
}

function formatTabLabel(identifier: ProcessedFileIdentifier): string {
  return identifier.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const documentRenderErrorFallback = (
  <div data-testid="document-render-error" className="text-center text-destructive py-8">
    This document could not be displayed.
  </div>
);

const ProcessedFilesTabs = ({
  files,
  isLoading,
  error,
  allHighlights,
  activeFileIdentifier,
  scrollTrigger,
  indications = NO_INDICATIONS,
}: ProcessedFilesTabsProps) => {
  const [activeTab, setActiveTab] = useState(files[0]?.id || "");
  const { tooltip, handleMouseOver, handleMouseOut } = useEvidenceTooltip();
  const [focusedMention, setFocusedMention] = useState<FocusedMention | null>(null);
  const [mentionScrollCount, setMentionScrollCount] = useState(0);
  // The evidence `scrollTrigger` when a mention was last opened. Evidence picked since then bumps it, so
  // the newest selection is derived in the same render the viewer sees, with no effect to lag behind.
  const [mentionOpenedAtTrigger, setMentionOpenedAtTrigger] = useState<number | null>(null);
  const lastSelected = mentionOpenedAtTrigger === (scrollTrigger ?? 0) ? "indication" : "evidence";

  // A refetch can change the positions, so a stale focus would point at a different indication.
  useEffect(() => {
    setFocusedMention(null);
  }, [indications]);

  // Set initial tab when files load asynchronously
  useEffect(() => {
    if (files.length > 0 && !activeTab) {
      setActiveTab(files[0].id);
    }
  }, [files, activeTab]);

  // When activeFileIdentifier changes, switch to that tab
  useEffect(() => {
    if (activeFileIdentifier) {
      const targetFile = files.find((f) => f.fileIdentifier === activeFileIdentifier);
      if (targetFile) setActiveTab(targetFile.id);
    }
  }, [activeFileIdentifier, files]);

  const indicationHighlights = useMemo(
    () => buildIndicationHighlights(indications, lastSelected === "indication" ? focusedMention : null),
    [indications, focusedMention, lastSelected],
  );

  const mentionByKey = useMemo(
    () =>
      new Map(
        indications.flatMap((verdict) =>
          verdict.mentions.map(
            (mention, mentionIndex) => [mentionKeyOf(verdict.position, mentionIndex), mention] as const,
          ),
        ),
      ),
    [indications],
  );

  // The document each mention was found in. A stored source key names it exactly, and a source that did not load
  // leaves the mention unfound rather than borrowing another document; only a mention with no key falls back to its type.
  const documentByMentionKey = useMemo(() => {
    const highlightsByCandidate = new Map<string, EvidenceHighlight[]>();
    for (const hl of buildIndicationHighlights(indications, null)) {
      const mention = hl.indication && mentionByKey.get(hl.indication.key);
      if (!mention) continue;

      const candidates = mention.objectKey
        ? files.filter((f) => f.objectKey === mention.objectKey)
        : files.filter((f) => f.fileIdentifier === mention.fileIdentifier);
      for (const file of candidates) {
        highlightsByCandidate.set(file.id, [...(highlightsByCandidate.get(file.id) ?? []), hl]);
      }
    }

    const documents = new Map<string, string>();
    for (const file of files) {
      const forFile = highlightsByCandidate.get(file.id);
      if (!forFile || file.textType === "base64") continue;

      const unresolved = unresolvedHighlightIndexes(file.text, isMarkdownFile(file), forFile);
      forFile.forEach((hl, index) => {
        if (hl.indication && !unresolved.has(index) && !documents.has(hl.indication.key)) {
          documents.set(hl.indication.key, file.id);
        }
      });
    }

    return documents;
  }, [files, indications, mentionByKey]);

  // Each viewer gets its own highlights: evidence by document type, an indication only on the document it was found in.
  const highlightsByFileId = useMemo(() => {
    const evidence = (allHighlights ?? []).map((hl) =>
      lastSelected === "indication" && hl.isActiveQuote ? { ...hl, isActiveQuote: false } : hl,
    );

    return new Map(
      files.map((file) => [
        file.id,
        [
          ...evidence.filter((hl) => hl.fileIdentifier === file.fileIdentifier),
          ...indicationHighlights.filter(
            (hl) => hl.indication && documentByMentionKey.get(hl.indication.key) === file.id,
          ),
        ],
      ]),
    );
  }, [files, allHighlights, indicationHighlights, documentByMentionKey, lastSelected]);

  const unfoundMentionKeys = useMemo(
    () =>
      new Set(
        [...mentionByKey.keys()].filter(
          (key) => !documentByMentionKey.has(key) && mentionByKey.get(key)?.fileIdentifier != null,
        ),
      ),
    [mentionByKey, documentByMentionKey],
  );

  const openMention = (position: number, mentionIndex: number) => {
    const fileId = documentByMentionKey.get(mentionKeyOf(position, mentionIndex));
    if (!fileId) return;

    setMentionOpenedAtTrigger(scrollTrigger ?? 0);
    setFocusedMention({ position, mentionIndex });
    setMentionScrollCount((n) => n + 1);
    setActiveTab(fileId);
  };

  const hasIndications = indications.length > 0;

  if (isLoading) {
    return <div className="text-center text-gray-500 py-8">Loading documents...</div>;
  }

  if (error) {
    return <div className="text-center text-destructive py-8">{error}</div>;
  }

  if (!files || files.length === 0) {
    return <div className="text-center text-gray-500 py-8">No medical documents found.</div>;
  }

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
        <TabsList className="flex h-auto w-full flex-wrap gap-1">
          {files.map((file) => (
            <TabsTrigger key={file.id} value={file.id} className="text-xs whitespace-nowrap">
              {file.title || formatTabLabel(file.fileIdentifier)}
            </TabsTrigger>
          ))}
          {hasIndications && (
            <TabsTrigger value={INDICATIONS_TAB} className="text-xs whitespace-nowrap">
              Indications ({indications.length})
            </TabsTrigger>
          )}
        </TabsList>

        {files.map((file) => (
          <TabsContent key={file.id} value={file.id} className="flex-1 min-h-0 min-w-0">
            <div className="border rounded-lg bg-white h-full flex flex-col min-w-0">
              <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4 min-h-0">
                <ErrorBoundary fallback={documentRenderErrorFallback}>
                  {file.textType === "base64" ? (
                    <Document
                      file={{ data: atob(file.text) }}
                      loading={<div>Loading PDF...</div>}
                      error={<div>Failed to load PDF.</div>}
                    >
                      <Page pageNumber={1} />
                    </Document>
                  ) : (
                    // biome-ignore lint/a11y/useKeyWithMouseEvents: the .evidence-hl spans this delegates to are not focusable, so an onFocus pair would never fire
                    // biome-ignore lint/a11y/noStaticElementInteractions: the hover is delegated from those same spans, not from this wrapper
                    <div onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                      <DocumentViewer
                        key={file.id}
                        content={file.text}
                        className="text-sm"
                        markdown={isMarkdownFile(file)}
                        highlights={highlightsByFileId.get(file.id)}
                        // Scrolling to a quote relies on inactive tabs not mounting their viewer (Radix default, no forceMount).
                        highlightVersion={(scrollTrigger ?? 0) + mentionScrollCount}
                      />
                    </div>
                  )}
                </ErrorBoundary>
              </div>
            </div>
          </TabsContent>
        ))}

        {hasIndications && (
          <TabsContent value={INDICATIONS_TAB} className="flex-1 min-h-0 min-w-0">
            <div className="border rounded-lg bg-white h-full overflow-y-auto p-4">
              <IndicationsPanel
                verdicts={indications}
                unfoundMentionKeys={unfoundMentionKeys}
                onOpenMention={openMention}
              />
            </div>
          </TabsContent>
        )}
      </Tabs>
      {createPortal(<EvidenceTooltip tooltip={tooltip} />, document.body)}
    </>
  );
};

export default ProcessedFilesTabs;
